import { useQuery } from 'react-query';
import React, { useState } from 'react';
import { QueryKey } from 'api/queryKey';
import Column from 'antd/lib/table/Column';
import { Checkbox } from 'antd';
import {
  Patient,
  patientsInitialPageParams,
  PatientType,
} from 'models/patient';
import { useEnsureContext } from 'common/hooks';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Link } from 'react-router-dom';
import { PatientsFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerPatientsManagement/BusinessPartnerPatientsPage/PatientsFilters';
import { purge } from 'lib/ramda/purge';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { BusinessPartnerRoutes } from 'pages/BusinessPartnerManagement/businessPartnerRoutes';
import { NumberedTable } from 'common/components/NumberedTable';
import { commonPaginationConfig } from 'models/page';
import { PartnerDetailsDTO } from 'models/higoDevice';
import styled from 'styled-components';
import { LoadingOutlined } from '@ant-design/icons';
import { isNil } from 'ramda';
import { useRole } from 'common/hooks/useRole';

interface MedicalOperatorPatientsToAssignList {
  checkedPatientsIds: number[];
  setCheckedPatientsIds: React.Dispatch<React.SetStateAction<number[]>>;
  partnerHardcoded: PartnerDetailsDTO | undefined;
  medicalOperatorId: number;
}

const CheckAll = styled.div`
  height: 45px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .label {
    margin-bottom: 5px;
  }
`;

export const MedicalOperatorPatientsToAssignList: React.FC<MedicalOperatorPatientsToAssignList> = ({
  checkedPatientsIds,
  setCheckedPatientsIds,
  partnerHardcoded,
  medicalOperatorId,
}) => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const [pageMetaData, setPageMetaData] = useState(patientsInitialPageParams);
  const [filters, setFilters] = useState({
    partnerName: partnerHardcoded?.name,
  });

  const { isFetching, data, isLoading } = useQuery(
    [QueryKey.BusinessPartnerPatientsList, pageMetaData, filters],
    () =>
      BusinessPartnersAPI.fetchPatients(
        partnerHardcoded && partnerHardcoded?.id !== 0
          ? partnerHardcoded?.id
          : bpContext.id,
        {
          ...pageMetaData,
          ...filters,
          patientType: PatientType.Pro,
        }
      ),
    {
      keepPreviousData: true,
    }
  );

  const intlMessage = useFormatMessage();

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const shouldShowPagination = data?.content && data?.totalElements > 20;

  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  return (
    <>
      <PatientsFilters
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        style={{ marginBottom: '1rem' }}
        partnerHardcoded={partnerHardcoded?.name}
        onSubmit={(values) => {
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters(purge(values));
        }}
      />
      <NumberedTable
        rowKey="id"
        loading={isFetching}
        dataSource={data?.content}
        pagination={shouldShowPagination ? paginationConfig : false}
        scroll={{ x: 'auto' }}
      >
        <Column
          title={() => (
            <>
              {data && data.totalElements > 0 && (
                <CheckAll>
                  {isLoading ? (
                    <LoadingOutlined />
                  ) : (
                    <>
                      <Checkbox
                        indeterminate={
                          checkedPatientsIds.length > 0 &&
                          checkedPatientsIds.length !== data?.totalElements
                        }
                        checked={
                          checkedPatientsIds.length > 0 &&
                          checkedPatientsIds.length === data?.totalElements
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            data?.content &&
                              setCheckedPatientsIds(
                                data.content.map((b) => b.id)
                              );
                            // : setFetchAllPatientsEnabled(true);
                          } else {
                            setCheckedPatientsIds([]);
                          }
                        }}
                      />
                      {`${checkedPatientsIds.length} / ${
                        data?.totalElements || '-'
                      }`}
                    </>
                  )}
                </CheckAll>
              )}
            </>
          )}
          key="actions"
          align="center"
          width={100}
          render={(value, record: Patient) => (
            <Checkbox
              key={record.id}
              onChange={(e) => {
                if (e.target.checked) {
                  setCheckedPatientsIds((ids: number[]) => [...ids, record.id]);
                } else {
                  setCheckedPatientsIds((ids: number[]) =>
                    ids.filter((id) => id !== record.id)
                  );
                }
              }}
              checked={checkedPatientsIds.includes(record.id)}
            />
          )}
        />
        <Column
          title={intlMessage({ id: 'name' })}
          dataIndex="firstName"
          render={(value, record: Patient) => (
            <Link
              key={record.id}
              to={`./${BusinessPartnerRoutes.Patients}/${record.id}`}
            >
              {record.firstName + ' ' + record.lastName}
            </Link>
          )}
        />
        <Column
          title={intlMessage({ id: 'person.personalId' })}
          dataIndex="idNumber"
        />
        {!shouldHidePartnerColumn && (
          <Column
            title={intlMessage({ id: 'partner' })}
            dataIndex={['partnerDetails', 'name']}
          />
        )}
        <Column
          title={intlMessage({ id: 'assigned_to' })}
          dataIndex="type"
          render={(value, record: Patient) => (
            <p>
              {value === 'PRO' &&
                record.assignedMedicalOperator &&
                `${record.assignedMedicalOperator?.firstName} ${record.assignedMedicalOperator?.lastName} `}
              {value === 'PRO' && !record.assignedMedicalOperator && '-'}
              {value === 'FAMILY' &&
                record.assignedFamilyUser &&
                `${record.assignedFamilyUser?.firstName} ${record.assignedFamilyUser?.lastName} `}
              {value === 'FAMILY' && !record.assignedFamilyUser && '-'}
            </p>
          )}
        />
      </NumberedTable>
    </>
  );
};
